// RPT2 (docs/build-briefs/RPT2-menu-profitability.md; PRD P0.13; design/mockups/rpt-menu-profitability/): the body
// of the Menu profitability report, drawn inside the Reports group's own frame (ui/src/reports.ts owns the title, the
// range chips and the location picker). This module only draws what the server sent and reports every tap back through
// `handlers`.
//
// Admin surface, touch-sized: every control is a 44px button, nothing depends on hover, nothing is in a tooltip and
// there is no keyboard listener. Text is set with textContent only (drink names are user-entered). A laptop shows the
// table; below 700px the same rows are one expandable card each (the stylesheet swaps them, so there is no sideways
// scroll at phone width).

import type {
  MenuProfitabilityDrinkDto,
  MenuProfitabilityReportDto,
  MenuProfitabilityQuadrant,
} from "./api.ts";
import {
  MP_QUADRANTS,
  MP_SORTS,
  dollars,
  isOverTarget,
  percent,
  priceCell,
  quadrantLabel,
  steppedTarget,
  targetText,
  visibleDrinks,
  wholeDollars,
  type MpSort,
} from "./menuProfitabilityModel.ts";

export interface MenuProfitabilityView {
  quadrant: MenuProfitabilityQuadrant | null;
  sort: MpSort;
  open: Set<string>;
  /** True while a target save is on its way: the stepper waits. */
  saving: boolean;
  saveFailed: boolean;
}

export interface MenuProfitabilityHandlers {
  onQuadrant: (quadrant: MenuProfitabilityQuadrant) => void;
  onSort: (sort: MpSort) => void;
  onStep: (targetBp: number) => void;
  onToggleOpen: (key: string) => void;
}

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) element.setAttribute(key, value);
  for (const child of children) element.append(child);
  return element;
}

function drinkKey(drink: MenuProfitabilityDrinkDto): string {
  return drink.menuDrinkId ?? `ref:${drink.name}`;
}

/** The pills after a drink's name: its quadrant, or No cost; Partly costed beside it. */
function pills(drink: MenuProfitabilityDrinkDto): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (!drink.costed) {
    out.push(h("span", { cls: "mp-pill mute", text: "No cost", testid: "mp-pill-nocost" }));
    return out;
  }
  const label = quadrantLabel(drink.quadrant);
  const tone = MP_QUADRANTS.find((q) => q.id === drink.quadrant)?.tone ?? "mute";
  if (label !== null)
    out.push(h("span", { cls: `mp-pill ${tone}`, text: label, testid: "mp-pill-quadrant" }));
  if (drink.partlyCosted) {
    out.push(h("span", { cls: "mp-pill mute", text: "Partly costed", testid: "mp-pill-partly" }));
  }
  return out;
}

function tiles(
  report: MenuProfitabilityReportDto,
  view: MenuProfitabilityView,
  handlers: MenuProfitabilityHandlers,
): HTMLElement {
  const wrap = h("div", { cls: "mp-qs", testid: "mp-quadrants" });
  for (const q of MP_QUADRANTS) {
    const tile = report.quadrants.find((t) => t.quadrant === q.id);
    const on = view.quadrant === q.id;
    const button = h(
      "button",
      {
        cls: `mp-q${on ? " on" : ""}`,
        testid: `mp-quadrant-${q.id}`,
        attrs: { type: "button", "aria-pressed": String(on) },
      },
      [
        h("span", { cls: "k" }, [h("i", { cls: `mp-dot ${q.tone}` }), q.label]),
        h("span", { cls: "v" }, [
          h("span", { text: String(tile?.drinks ?? 0), testid: `mp-quadrant-${q.id}-count` }),
          h("small", {
            text: `${wholeDollars(tile?.totalMarginCents ?? 0)} margin`,
            testid: `mp-quadrant-${q.id}-margin`,
          }),
        ]),
        h("span", { cls: "d", text: q.detail }),
      ],
    );
    button.addEventListener("click", () => handlers.onQuadrant(q.id));
    wrap.append(button);
  }
  return wrap;
}

function tools(
  report: MenuProfitabilityReportDto,
  view: MenuProfitabilityView,
  handlers: MenuProfitabilityHandlers,
): HTMLElement {
  const target = report.target;
  const bounds = { minBp: target.minBp, maxBp: target.maxBp, stepBp: target.stepBp };
  const down = steppedTarget(target.pourCostBp, -1, bounds);
  const up = steppedTarget(target.pourCostBp, 1, bounds);
  const step = (label: string, sign: string, testid: string, next: number | null): HTMLElement => {
    const button = h("button", {
      cls: "mp-step-btn",
      text: sign,
      testid,
      attrs: {
        type: "button",
        "aria-label": label,
        ...(next === null || view.saving ? { disabled: "" } : {}),
      },
    });
    if (next !== null) button.addEventListener("click", () => handlers.onStep(next));
    return button;
  };
  const stepper = h("span", { cls: "mp-step", testid: "mp-target" }, [
    step("Lower target pour cost", "−", "mp-target-down", down),
    h("span", {
      cls: "mp-step-value",
      text: targetText(target.pourCostBp),
      testid: "mp-target-value",
    }),
    step("Raise target pour cost", "+", "mp-target-up", up),
  ]);
  const sort = h("div", { cls: "mp-sort", testid: "mp-sorts" }, [h("span", { text: "Sort" })]);
  for (const s of MP_SORTS) {
    const on = view.sort === s.id;
    const chip = h("button", {
      cls: `chip${on ? " on" : ""}`,
      text: s.label,
      testid: `mp-sort-${s.id}`,
      attrs: { type: "button", "aria-pressed": String(on) },
    });
    chip.addEventListener("click", () => handlers.onSort(s.id));
    sort.append(chip);
  }
  const left = h("div", { cls: "mp-tgt" }, [h("span", { text: "Target pour cost" }), stepper]);
  if (view.saveFailed)
    left.append(h("span", { cls: "mp-error", text: "Not saved", testid: "mp-target-error" }));
  return h("div", { cls: "mp-tools" }, [left, sort]);
}

function cell(text: string, cls = "", testid?: string): HTMLElement {
  return h("td", { cls, text, ...(testid === undefined ? {} : { testid }) });
}

function priceTd(drink: MenuProfitabilityDrinkDto): HTMLElement {
  const price = priceCell(drink);
  if (price.kind === "pill") {
    return h("td", { testid: "mp-price" }, [h("span", { cls: "mp-pill ok", text: price.text })]);
  }
  return cell(price.text, price.kind === "na" ? "na" : "hit", "mp-price");
}

function tableRow(drink: MenuProfitabilityDrinkDto): HTMLElement {
  const na = drink.costed ? "" : "na";
  const row = h("tr", { testid: `mp-row-${drink.name}` });
  row.append(
    h("td", { cls: "l" }, [h("span", { text: drink.name, testid: "mp-name" }), ...pills(drink)]),
  );
  row.append(
    cell(String(drink.sold), "", "mp-sold"),
    cell(wholeDollars(drink.salesCents), "", "mp-revenue"),
    cell(percent(drink.shareBp), "", "mp-share"),
    cell(dollars(drink.costPerDrinkCents), na, "mp-cost"),
    cell(
      percent(drink.pourCostBp),
      drink.costed ? (isOverTarget(drink) ? "hi" : "") : "na",
      "mp-pour",
    ),
    cell(dollars(drink.marginPerDrinkCents), na, "mp-margin"),
    cell(wholeDollars(drink.totalMarginCents), drink.costed ? "strong" : "na", "mp-total"),
    priceTd(drink),
  );
  return row;
}

function table(drinks: MenuProfitabilityDrinkDto[]): HTMLElement {
  const head = h("tr", {}, [
    h("th", { cls: "l", text: "Drink" }),
    ...[
      "Sold",
      "Revenue",
      "Share",
      "Cost",
      "Pour %",
      "Margin",
      "Total margin",
      "Price to hit target",
    ].map((t) => h("th", { text: t })),
  ]);
  return h("div", { cls: "mp-card mp-table-card" }, [
    h("table", { cls: "mp-table", testid: "mp-table" }, [
      h("thead", {}, [head]),
      h("tbody", {}, drinks.map(tableRow)),
    ]),
  ]);
}

function stat(label: string, value: string, cls = "", testid?: string): HTMLElement {
  return h("div", {}, [
    label,
    h("span", { cls, text: value, ...(testid === undefined ? {} : { testid }) }),
  ]);
}

function phoneCard(
  drink: MenuProfitabilityDrinkDto,
  view: MenuProfitabilityView,
  handlers: MenuProfitabilityHandlers,
): HTMLElement {
  const key = drinkKey(drink);
  const open = view.open.has(key);
  const na = drink.costed ? "" : "na";
  const top = h(
    "button",
    {
      cls: "mp-dc-t",
      testid: `mp-card-toggle-${drink.name}`,
      attrs: { type: "button", "aria-expanded": String(open) },
    },
    [
      h("b", {}, [h("span", { text: drink.name }), ...pills(drink)]),
      h("span", { cls: "c", text: open ? "⌄" : "›", attrs: { "aria-hidden": "true" } }),
    ],
  );
  top.addEventListener("click", () => handlers.onToggleOpen(key));
  const card = h("div", { cls: "mp-dc", testid: `mp-card-${drink.name}` }, [
    top,
    h("div", { cls: "mp-l2" }, [
      stat("Sold", String(drink.sold)),
      stat(
        "Pour",
        percent(drink.pourCostBp),
        drink.costed ? (isOverTarget(drink) ? "hi" : "") : "na",
      ),
      stat("Margin", dollars(drink.marginPerDrinkCents), na),
    ]),
  ]);
  if (open) {
    const price = priceCell(drink);
    const hit = h("div", { cls: "hitbox" }, ["Price to hit target"]);
    if (price.kind === "pill") {
      hit.append(h("span", {}, [h("span", { cls: "mp-pill ok", text: price.text })]));
    } else {
      const value = h("span", { text: price.text, testid: "mp-card-price" });
      if (price.kind === "price") {
        value.append(" ", h("small", { cls: "sub", text: `now ${dollars(drink.avgPriceCents)}` }));
      }
      hit.append(value);
    }
    card.append(
      h("div", { cls: "mp-more", testid: `mp-card-more-${drink.name}` }, [
        stat("Revenue", wholeDollars(drink.salesCents)),
        stat("Share", percent(drink.shareBp)),
        stat("Cost", dollars(drink.costPerDrinkCents), na),
        stat("Total margin", wholeDollars(drink.totalMarginCents), na),
        hit,
      ]),
    );
  }
  return card;
}

/** Draws the whole body into `host`. */
export function renderMenuProfitability(
  host: HTMLElement,
  report: MenuProfitabilityReportDto,
  view: MenuProfitabilityView,
  handlers: MenuProfitabilityHandlers,
): void {
  const root = h("div", { cls: "mp", testid: "mp-root" });
  root.append(tiles(report, view, handlers), tools(report, view, handlers));
  const drinks = visibleDrinks(report.drinks, view.sort, view.quadrant);
  if (drinks.length === 0) {
    root.append(h("p", { cls: "mp-empty", text: "No drinks match this view. Change the filter or date range.", testid: "mp-empty" }));
  } else {
    root.append(table(drinks));
    root.append(
      h(
        "div",
        { cls: "mp-card mp-cards", testid: "mp-cards" },
        drinks.map((d) => phoneCard(d, view, handlers)),
      ),
    );
  }
  host.replaceChildren(root);
}
